// COST-01: the job profit / margin bar. Price on the full width; the cost
// fills from the left (estimated as a marker, actual as the bar).
import { formatMoney } from '@/lib/format'

export interface MarginThresholds { redBelow: number; greenAt: number }
/** Workspace defaults (Settings → Team → Margin targets): red < 26%, amber 26–<33%, green ≥ 33%. */
export const DEFAULT_THRESHOLDS: MarginThresholds = { redBelow: 26, greenAt: 33 }

export function marginTone(margin: number | null, t: MarginThresholds = DEFAULT_THRESHOLDS): 'good' | 'warn' | 'bad' | 'none' {
  if (margin == null) return 'none'
  if (margin < t.redBelow) return 'bad'
  if (margin < t.greenAt) return 'warn'
  return 'good'
}

const TONE_COLOR = { good: 'var(--mint-text)', warn: 'var(--butter-text)', bad: 'var(--blush-text)', none: 'rgb(var(--fg-rgb) / 0.3)' }

function pct(part: number, whole: number) {
  if (whole <= 0) return part > 0 ? 100 : 0
  return Math.max(0, Math.min(100, (part / whole) * 100))
}

export function JobProfitBar({
  price,
  estimatedCost,
  actualCost,
  marginPercent,
  estimatedMarginPercent,
  thresholds,
}: {
  thresholds?: MarginThresholds
  price: number
  estimatedCost: number
  actualCost: number
  marginPercent: number | null
  estimatedMarginPercent: number | null
}) {
  const tone = marginTone(marginPercent, thresholds)
  const profit = price - actualCost
  return (
    <div data-testid="job-profit-bar" data-tone={tone}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: TONE_COLOR[tone] }}>
          {marginPercent == null ? 'No price yet' : `${marginPercent}% margin`}
          <span style={{ fontWeight: 400, color: 'var(--midnight-text3)' }}> · {formatMoney(profit)} profit</span>
        </span>
        <span style={{ fontSize: 12, color: 'var(--midnight-text3)' }}>
          Estimated margin {estimatedMarginPercent == null ? '—' : `${estimatedMarginPercent}%`}
        </span>
      </div>
      <div
        role="meter"
        aria-label="Job cost against price"
        aria-valuetext={`Actual cost ${formatMoney(actualCost)}; estimated cost ${formatMoney(estimatedCost)}; price ${formatMoney(price)}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct(actualCost, price))}
        style={{ position: 'relative', height: 10, borderRadius: 6, background: 'rgb(var(--fg-rgb) / 0.08)', overflow: 'hidden' }}
      >
        <div style={{ width: `${pct(actualCost, price)}%`, height: '100%', background: TONE_COLOR[tone] }} />
        <div
          title="Estimated cost"
          style={{ position: 'absolute', top: 0, bottom: 0, left: `clamp(0px, ${pct(estimatedCost, price)}%, calc(100% - 2px))`, width: 2, background: 'rgb(var(--fg-rgb) / 0.6)' }}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, fontSize: 12, color: 'var(--midnight-text3)', marginTop: 4 }}>
        <span>Actual cost {formatMoney(actualCost)}</span>
        <span>Estimated cost {formatMoney(estimatedCost)}</span>
        <span>Price {formatMoney(price)}</span>
      </div>
    </div>
  )
}
